import { useState } from "react";
import { useDispatch } from "react-redux";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { showError, showSuccessToast } from "@stadium/services/react-toastify";
import { getUpdatedDummyStoreFields } from "@helpers/create-shop";

import { updateUserDummyStore, publishUserDummyStore } from "apis/user-shop";
import { setStore } from "@slices/store";
import { useJuneSoEventTracking } from "@stadium/services/june-analytics/hooks/use-june-so-event-tracking";
import { useWorkspaceContext } from "@contexts/workspace-context";

import { externalRoutes } from "@constants/external-routes";
import {
  SHOP_UPDATED_MESSAGE,
  SHOP_CREATION_ERROR_MESSAGE,
  SHOP_UPDATION_ERROR_MESSAGE,
} from "@constants/store";
import { CREATE_SHOP, DEFAULT_SERVICES } from "@constants/june-so";

const useUpdateDummyStore = (number) => {
  const { push } = useRouter();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { getDashboardUrlWithCurrentWorkspaceSlug } = useWorkspaceContext();
  const { trackEvent } = useJuneSoEventTracking({ services: DEFAULT_SERVICES });

  const updateDummyStore = ({
    data,
    successMsg = SHOP_UPDATED_MESSAGE,
    successCb,
    showSuccessMessage = true,
    successTitle = "",
  }) => {
    const payload = {
      number,
      ...data,
    };
    setLoading(true);

    return updateUserDummyStore(payload)
      .then((res) => {
        const { dummyStore, errors } = res || {};
        if (dummyStore) {
          dispatch(
            setStore({
              ...getUpdatedDummyStoreFields(dummyStore, false),
              ...(data.bannerMetadata?.shouldUpdateBanner && { banner: dummyStore.banner }),
            }),
          );
          successCb?.();
          showSuccessMessage && showSuccessToast({ message: successMsg, title: successTitle });
          return;
        }
        if (errors?.length) {
          return errors.map(showError);
        }
        showError(SHOP_UPDATION_ERROR_MESSAGE);
      })
      .finally(() => setLoading(false));
  };

  const publishDummyStore = ({ data = {}, successCb, errorCb }) => {
    setLoading(true);
    publishUserDummyStore({
      number,
      ...data,
    })
      .then((data) => {
        const { entity, errors } = data || {};
        if (entity?.number) {
          const { number: shopNumber, name } = entity;
          const eventProperties = { shop_id: shopNumber, shop_name: name };
          trackEvent({
            componentName: CREATE_SHOP,
            eventProperties,
            action: "created",
          });
          successCb();
          return push(
            getDashboardUrlWithCurrentWorkspaceSlug(
              externalRoutes.storeDashboard({ number: entity?.number }),
            ),
          );
        }
        if (errors?.length) {
          errorCb?.();
          return errors.map(showError);
        }
        showError(SHOP_CREATION_ERROR_MESSAGE);
      })
      .finally(() => setLoading(false));
  };

  return { updateDummyStore, loading, publishDummyStore };
};

export default useUpdateDummyStore;
